import React, { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';

import { routes } from '@/navigation/routes.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import SettingsShape from '@/components/common/svg/shapes/SettingsShape.tsx';
import { nameAgeStyle } from '@/components/Discovery/Card/Components/Bio.tsx';
import usePolicy from '@/hooks/usePolicy.ts';
import ErrorShape from '@/components/common/svg/shapes/ErrorShape.tsx';
import GhostShape from '@/components/common/svg/shapes/GhostShape.tsx';
import HiddenShape from '@/components/common/svg/shapes/HiddenShape.tsx';
import VerifiedBadgeShape from '@/components/common/svg/shapes/VerifiedBadgeShape.tsx';
import ClockShape from '@/components/common/svg/shapes/ClockShape.tsx';
import EditShape from '@/components/common/svg/shapes/EditShape.tsx';
import { AvatarWithProgressBar } from '@/components/Profile/Header/AvatarWithProgressBar/AvatarWithProgressBar.tsx';
import { helpers } from '@/helpers.ts';
import { thumbHashToImageUrl } from '@/utils/thumbhash.ts';

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const { aboutMe, getProfileCompletionPercentage } = useProfileStore();
  const {
    isActive,
    isHidden,
    isInsufficientData,
    isBeingVerified,
    isIncognitoMode
  } = usePolicy();

  const name = aboutMe?.profile?.name || '';
  const profileCompletion = getProfileCompletionPercentage();
  const firstPhotoVariants = aboutMe?.profile.v2photos?.[0]?.variants ?? [];
  const thumbhash = helpers.getThumbHash(firstPhotoVariants);
  const avatarFallback = thumbhash ? thumbHashToImageUrl(thumbhash) : undefined;

  return (
    <div style={containerStyle}>
      <div
        style={settingsButtonStyle}
        onClick={() => navigate(routes.profileSettings)}
      >
        <div style={elementBackdrop}>
          <SettingsShape width={30} height={30} fillColor="#000" />
        </div>
      </div>
      <div
        style={{ position: 'relative' }}
        onClick={() => navigate(routes.profileEdit)}
      >
        <AvatarWithProgressBar
          profilePhoto={helpers.getPrimaryPhoto(firstPhotoVariants)}
          fallback={avatarFallback}
          thumbhash={thumbhash}
          gender={aboutMe?.profile?.gender}
          profileCompletion={profileCompletion}
          containerStyle={avatarContainerStyle}
        />
        <div style={editButtonContainerStyle}>
          <EditShape width={14} height={14} fillColor="#FFF" />
          <div style={editDotStyle} />
        </div>
      </div>
      <div
        style={{
          ...nameAgeStyle,
          ...elementBackdrop,
          color: '#000000',
          fontWeight: 700
        }}
        onClick={() => navigate(routes.profileEdit)}
      >
        <div style={{ fontSize: 19 }}>
          {name}
          {aboutMe?.profile.age !== 0 ? `, ${aboutMe?.profile.age}` : ''}&nbsp;
        </div>
        <div style={elementBackdrop}>
          {isActive && <VerifiedBadgeShape height={22} width={22} />}
          {isInsufficientData && <ErrorShape fillColor="#FFC107" />}
          {isHidden && (
            <HiddenShape width={20} height={20} fillColor="#D3D3D3" />
          )}
          {isBeingVerified && (
            <ClockShape width={18} height={18} fillColor="#FFC107" />
          )}
          {isIncognitoMode && (
            <GhostShape
              fillColor="#D3D3D3"
              width={20}
              height={20}
              style={{ opacity: '0.8' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const containerStyle: CSSProperties = {
  minHeight: '35%',
  width: '100%',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center'
};

const settingsButtonStyle: CSSProperties = {
  position: 'absolute',
  top: '14px',
  right: '24px'
} as const;

const avatarContainerStyle: CSSProperties = {
  borderRadius: '60px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  background: '#e9e9e9'
};

const elementBackdrop: CSSProperties = {
  marginTop: 0,
  padding: 1,
  borderRadius: '15px',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center'
};

const editButtonContainerStyle: CSSProperties = {
  position: 'absolute',
  top: '-10px',
  right: '-1px',
  width: '30px',
  height: '30px',
  borderRadius: '50px',
  background: '#fff',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 10,
  cursor: 'pointer',
  boxShadow: '0px 0px 2px 0px #00000026'
};

const editDotStyle: CSSProperties = {
  position: 'absolute',
  top: '2px',
  right: '1px',
  width: '6px',
  height: '6px',
  backgroundColor: '#851989',
  borderRadius: '50%'
};